import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { relancerTwilio } from '@/lib/twilioDeviceSingleton'
import { relancerTelnyx } from '@/lib/voix/telnyx'
import { useConnexionStore } from '@/stores/useConnexionStore'
import { useEtatTelephoneStore, type EtatTelephone } from '@/stores/useEtatTelephoneStore'
import { useTabStateStore } from '@/stores/useTabStateStore'

/** A short drop (Wi-Fi change) comes back on its own: say nothing before this. */
const DELAI_AVANT_AFFICHAGE_MS = 5_000
/** Still reconnecting after this long: offer the Reconnect button too. */
const DELAI_AVANT_BOUTON_MS = 60_000

/**
 * The web phone lost its connection (Telnyx recommends showing it): "Reconnecting
 * the phone…" while it comes back on its own, then "The phone is disconnected"
 * with a Reconnect button when several attempts failed. Hidden when the server
 * itself is unreachable (BandeauConnexion says it) or another tab has the phone.
 */
export function BandeauTelephone() {
  const { t } = useTranslation()
  const twilio = useEtatTelephoneStore((s) => s.twilio)
  const telnyx = useEtatTelephoneStore((s) => s.telnyx)
  const ongletActif = useTabStateStore((s) => s.isActive)
  const injoignable = useConnexionStore((s) => s.injoignable)

  const pire = (etats: EtatTelephone[]): 'coupe' | 'reconnexion' | null =>
    etats.includes('coupe') ? 'coupe' : etats.includes('reconnexion') ? 'reconnexion' : null
  const etatBrut = pire([twilio, telnyx])

  // "Reconnecting" only once it lasts; "disconnected" right away, or after a
  // minute of reconnecting.
  const [visible, setVisible] = useState(false)
  const [long, setLong] = useState(false)
  useEffect(() => {
    setLong(false)
    if (etatBrut !== 'reconnexion') {
      setVisible(etatBrut === 'coupe')
      return
    }
    const minuteurs = [
      window.setTimeout(() => setVisible(true), DELAI_AVANT_AFFICHAGE_MS),
      window.setTimeout(() => setLong(true), DELAI_AVANT_BOUTON_MS),
    ]
    return () => minuteurs.forEach((m) => window.clearTimeout(m))
  }, [etatBrut])
  const etat = etatBrut === 'reconnexion' && long ? 'coupe' : etatBrut

  if (!etat || !visible || !ongletActif || injoignable) return null

  const reconnecter = () => {
    relancerTwilio()
    relancerTelnyx()
  }

  return (
    <div
      role="status"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-32px)] max-w-md rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 shadow-md dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <div className="flex items-center gap-3">
        {etat === 'reconnexion' && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
        <p className="flex-1 font-medium">
          {etat === 'coupe'
            ? t('bandeauTelephone.coupe', 'Le téléphone est déconnecté.')
            : t('bandeauTelephone.reconnexion', 'Reconnexion du téléphone…')}
        </p>
        {etat === 'coupe' && (
          <button
            type="button"
            onClick={reconnecter}
            className="shrink-0 rounded-md border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-600 dark:bg-amber-900 dark:text-amber-50 dark:hover:bg-amber-800"
          >
            {t('bandeauTelephone.reconnecter', 'Reconnecter')}
          </button>
        )}
      </div>
    </div>
  )
}
